/**
 * Shared SVG parsing, sanitizing, and id-prefixing helpers.
 * Used by insert-decorative-svg.js (decorative library) and the element
 * insertion engine (social-icon library) so both consume one copy of the
 * sanitizer logic.
 */

function getParser() {
  if (typeof DOMParser === 'undefined') {
    throw new Error('DOMParser is required to parse SVG markup');
  }
  return new DOMParser();
}

function getSerializer() {
  if (typeof XMLSerializer === 'undefined') return null;
  return new XMLSerializer();
}

/**
 * Parse a string of SVG markup. Returns the <svg> element, or null on
 * parser error / non-svg root.
 */
export function parseSvg(svgMarkup) {
  const doc = getParser().parseFromString(String(svgMarkup || ''), 'image/svg+xml');
  if (doc.querySelector('parsererror')) return null;
  const svg = doc.documentElement;
  if (!svg || svg.tagName.toLowerCase() !== 'svg') return null;
  return svg;
}

/**
 * Remove dangerous attributes and elements from an SVG subtree in place.
 * - Strips `on*` event handlers from every element.
 * - Removes <script>, <style>, <foreignObject>, <iframe>, <object>, <embed>.
 * - Rewrites javascript: href / xlink:href to nothing.
 * Returns the same node (mutated).
 */
export function sanitizeSvg(svg) {
  for (const attr of Array.from(svg.attributes || [])) {
    if (attr.name.toLowerCase().startsWith('on')) svg.removeAttribute(attr.name);
  }

  for (const el of Array.from(
    svg.querySelectorAll('script, style, foreignObject, iframe, object, embed')
  )) {
    el.remove();
  }

  for (const el of Array.from(svg.querySelectorAll('*'))) {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim().toLowerCase();
      if (name.startsWith('on')) {
        el.removeAttribute(attr.name);
        continue;
      }
      if ((name === 'href' || name === 'xlink:href') && value.startsWith('javascript:')) {
        el.removeAttribute(attr.name);
      }
    }
  }

  return svg;
}

/**
 * Rewrite every `id` inside `svg` to be unique by prefixing it, and rewrite
 * every internal `url(#id)` / `#id` / `"#id"` reference to match. Used to
 * avoid id collisions when the same SVG is inlined more than once on a page.
 */
export function prefixSvgIds(svg, prefix) {
  const idMap = new Map();
  for (const el of Array.from(svg.querySelectorAll('[id]'))) {
    const oldId = el.getAttribute('id');
    if (!oldId) continue;
    const nextId = `${prefix}-${oldId}`;
    idMap.set(oldId, nextId);
    el.setAttribute('id', nextId);
  }

  if (idMap.size === 0) return;

  for (const el of Array.from(svg.querySelectorAll('*'))) {
    for (const attr of Array.from(el.attributes)) {
      let next = attr.value;
      for (const [oldId, newId] of idMap) {
        next = next
          .replaceAll(`url(#${oldId})`, `url(#${newId})`)
          .replaceAll(`"#${oldId}"`, `"#${newId}"`)
          .replaceAll(`'#${oldId}'`, `'#${newId}'`);
        if (next === `#${oldId}`) next = `#${newId}`;
      }
      if (next !== attr.value) el.setAttribute(attr.name, next);
    }
  }
}

/**
 * Serialize an SVG element to a string. Falls back to outerHTML if no
 * XMLSerializer is available (e.g. in test environments without DOM).
 */
export function serializeSvg(svg) {
  if (!svg) return '';
  const serializer = getSerializer();
  return serializer ? serializer.serializeToString(svg) : svg.outerHTML;
}
